import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  DEFAULT_ROUTE_FLAGS,
  ROUTE_FLAG_KEYS,
  type RouteFlagKey,
} from "@/config/routeFlags";

const CACHE_KEY = "gglife.routeFlags.v1";

type RouteFlagMap = Record<RouteFlagKey, boolean>;

interface RouteFlagContextValue {
  flags: RouteFlagMap;
  loaded: boolean;
  refresh: () => Promise<void>;
}

const RouteFlagContext = createContext<RouteFlagContextValue>({
  flags: DEFAULT_ROUTE_FLAGS,
  loaded: false,
  refresh: async () => {},
});

function readCache(): RouteFlagMap {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return DEFAULT_ROUTE_FLAGS;
    const parsed = JSON.parse(raw) as Partial<RouteFlagMap>;
    return ROUTE_FLAG_KEYS.reduce((acc, key) => {
      acc[key] = typeof parsed[key] === "boolean" ? (parsed[key] as boolean) : DEFAULT_ROUTE_FLAGS[key];
      return acc;
    }, {} as RouteFlagMap);
  } catch {
    return DEFAULT_ROUTE_FLAGS;
  }
}

export function RouteFlagProvider({ children }: { children: ReactNode }) {
  const [flags, setFlags] = useState<RouteFlagMap>(() => readCache());
  const [loaded, setLoaded] = useState(false);

  const refresh = async () => {
    const { data, error } = await supabase
      .from("route_flags")
      .select("key, enabled");

    if (error || !data) {
      setLoaded(true);
      return;
    }

    const next = { ...DEFAULT_ROUTE_FLAGS };
    for (const row of data as { key: string; enabled: boolean }[]) {
      if ((ROUTE_FLAG_KEYS as string[]).includes(row.key)) {
        next[row.key as RouteFlagKey] = row.enabled;
      }
    }
    setFlags(next);
    setLoaded(true);
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify(next));
    } catch {
      /* storage unavailable — flags still work in memory */
    }
  };

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = useMemo<RouteFlagContextValue>(() => ({ flags, loaded, refresh }), [flags, loaded]);

  return <RouteFlagContext.Provider value={value}>{children}</RouteFlagContext.Provider>;
}

/** True when the route/feature is enabled (uses compile-time default until flags load). */
export function useRouteFlag(key: RouteFlagKey): boolean {
  return useContext(RouteFlagContext).flags[key];
}

export function useRouteFlags(): RouteFlagContextValue {
  return useContext(RouteFlagContext);
}
